/*
 * Brand glyphs lucide does not ship (it dropped brand icons in 2023). Each one
 * takes the same props as a lucide icon so it can sit in the same slots.
 * Paths are simplified single-colour marks; they render in `currentColor`
 * unless the brand's own colours are essential (Google, Microsoft).
 */
import type { LucideProps } from "lucide-react";
import React from "react";

type BrandIconProps = Omit<LucideProps, "ref">;

function svgProps(props: BrandIconProps) {
  const { size = 24, ...rest } = props;
  return {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    ...rest
  };
}

export const FacebookIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function FacebookIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="currentColor" stroke="none">
        <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.5h-2.8V24C19.62 23.1 24 18.1 24 12.07" />
      </svg>
    );
  }
);

export const InstagramIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function InstagramIcon(props, ref) {
    return (
      <svg
        ref={ref}
        {...svgProps(props)}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
      </svg>
    );
  }
);

export const LinkedinIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function LinkedinIcon(props, ref) {
    return (
      <svg
        ref={ref}
        {...svgProps(props)}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect width="4" height="12" x="2" y="9" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    );
  }
);

export const GoogleIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function GoogleIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="none" stroke="none">
        <path
          fill="#EA4335"
          d="M12 5.04c1.93 0 3.23.83 3.97 1.53l2.9-2.83C17.09 2.09 14.77 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.32 2.58C6.33 7.17 8.93 5.04 12 5.04z"
        />
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.9-.07-1.55-.22-2.23H12v4.05h6.05c-.12 1.01-.78 2.54-2.25 3.57l3.24 2.51c1.94-1.79 3.52-4.43 3.52-7.9z"
        />
        <path
          fill="#FBBC05"
          d="M5.5 14.35A6.9 6.9 0 0 1 5.14 12c0-.82.14-1.6.35-2.35L2.18 7.07A11.04 11.04 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.32-2.58z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.24-2.51c-.87.6-2.03 1.03-4.04 1.03-3.07 0-5.67-2.13-6.5-4.95l-3.32 2.58C4 20.53 7.7 23 12 23z"
        />
      </svg>
    );
  }
);

export const MicrosoftIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function MicrosoftIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="none" stroke="none">
        <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
        <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
        <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
        <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
      </svg>
    );
  }
);

export const OktaIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function OktaIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="currentColor" stroke="none">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" />
      </svg>
    );
  }
);

export const StripeIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function StripeIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="currentColor" stroke="none">
        <path d="M13.48 10.13c-1.43-.53-2.2-.94-2.2-1.6 0-.55.46-.87 1.27-.87 1.49 0 3.02.57 4.07 1.09l.6-3.67A10.8 10.8 0 0 0 12.9 4.3c-1.63 0-2.99.43-3.96 1.22a4.05 4.05 0 0 0-1.5 3.27c0 2.53 1.55 3.61 4.06 4.52 1.62.58 2.16.99 2.16 1.62 0 .62-.53 .97-1.48.97-1.18 0-3.13-.58-4.4-1.32l-.6 3.71c1.09.62 3.1 1.26 5.2 1.26 1.72 0 3.15-.41 4.12-1.19a4.13 4.13 0 0 0 1.53-3.39c0-2.6-1.59-3.68-4.55-4.84z" />
      </svg>
    );
  }
);

export const OneDriveIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function OneDriveIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="currentColor" stroke="none">
        <path d="M9.6 6a5.6 5.6 0 0 1 5.3 3.8A4.4 4.4 0 0 1 19.5 14a4.2 4.2 0 0 1-4.2 4H6.2A4.2 4.2 0 0 1 2 13.8a4.3 4.3 0 0 1 3.4-4.2A5.6 5.6 0 0 1 9.6 6z" />
      </svg>
    );
  }
);

export const GoogleDriveIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function GoogleDriveIcon(props, ref) {
    return (
      <svg ref={ref} {...svgProps(props)} fill="none" stroke="none">
        <path fill="#0066DA" d="M3.6 18.4 5 20.8c.3.5.7.9 1.2 1.2l3.9-6.8H2c0 .6.2 1.2.5 1.7l1.1 1.5z" />
        <path fill="#00AC47" d="M12 8.8 8.1 2c-.5.3-.9.7-1.2 1.2L2.5 10.9c-.3.5-.5 1.1-.5 1.7h8.1L12 8.8z" />
        <path fill="#EA4335" d="M17.8 22c.5-.3.9-.7 1.2-1.2l.6-1 2.9-5c.3-.5.5-1.1.5-1.7h-8.1l1.7 3.5 1.2 5.4z" />
        <path fill="#00832D" d="M12 8.8 15.9 2c-.5-.3-1.1-.5-1.7-.5H9.8c-.6 0-1.2.2-1.7.5L12 8.8z" />
        <path fill="#2684FC" d="M13.9 14.2h-3.8L6.2 22c.5.3 1.1.5 1.7.5h8.2c.6 0 1.2-.2 1.7-.5l-3.9-7.8z" />
        <path fill="#FFBA00" d="m17.5 7.4-2.9-5c-.3-.5-.7-.9-1.2-1.2L12 8.8l1.9 3.8H22c0-.6-.2-1.2-.5-1.7l-4-3.5z" />
      </svg>
    );
  }
);

export const WordIcon = React.forwardRef<SVGSVGElement, BrandIconProps>(
  function WordIcon(props, ref) {
    return (
      <svg
        ref={ref}
        {...svgProps(props)}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="m7.5 12 1.3 5 1.7-5 1.7 5 1.3-5" />
      </svg>
    );
  }
);
